/**
 * Ítem del menubar (modelo alineado con PrimeReact MenuItem / Menubar).
 * - Hoja: sin `items` → navegación o botón que emite `itemSelect` (equivalente a `command`).
 * - Rama: con `items` → despliega submenú bajo la barra; un hijo puede anidar otro `items`.
 * - Icono opcional en barra y en cada hoja del submenú: define `iconSrc` solo si aplica.
 */
export interface UiMenubarItem {
  id: string;
  label: string;
  /** Icono opcional (barra principal o ítems del submenú). Omitir si el ítem es solo texto. */
  iconSrc?: string | null;
  disabled?: boolean;
  /** Marca visual activa (util para maqueta y navegacion controlada por la app). */
  active?: boolean;
  /** Línea separadora (solo útil dentro de un submenú) */
  separator?: boolean;
  /** Submenú desplegable (raíz bajo la barra o flyout anidado) */
  items?: readonly UiMenubarItem[] | null;
  /** Navegación interna (Angular Router); en hojas sin `items` */
  routerLink?: string | null;
  /** Enlace externo o ancla */
  href?: string | null;
  hrefTarget?: '_self' | '_blank';
  hrefRel?: string | null;
}

/** Devuelve el primer destino navegable respetando el orden del menubar. */
export function firstMenubarRouterLink(
  items: readonly UiMenubarItem[] | undefined,
): string | null {
  for (const item of items ?? []) {
    if (item.routerLink) return item.routerLink;
    const nestedLink = firstMenubarRouterLink(item.items ?? undefined);
    if (nestedLink) return nestedLink;
  }
  return null;
}

/**
 * Devuelve los ítems con `active` puesto en el que coincide con `activeId`. Un
 * ítem de submenú activo marca también a su raíz de barra, para que la sección
 * quede señalada. Las ramas intermedias (grupos anidados) también reciben
 * `active` en el modelo; el template no les aplica el estilo de hoja activa.
 * Los App Shells la usan para resolver el menubar del marco de página sin
 * duplicar la lógica.
 */
export function markActiveMenubarItem(
  items: readonly UiMenubarItem[],
  activeId: string | null,
): readonly UiMenubarItem[] {
  if (!activeId) return items;
  return items.map(item => {
    const children = item.items ?? null;
    if (children?.length) {
      const marked = markActiveMenubarItem(children, activeId);
      return {
        ...item,
        items: marked,
        // Activo si coincide la raíz o alguna hoja del submenú.
        active: item.id === activeId || marked.some(child => child.active),
      };
    }
    return item.id === activeId ? { ...item, active: true } : item;
  });
}
